import React from "react";
import { useLocation } from "react-router-dom";
import constants from "../../Util/constants";

// SEC-05. Flywire has two environments: 'prod' takes real money, 'demo' is a sandbox
// where test cards work and nothing is ever charged. constants.js used to default to
// 'demo' — a leftover from a test window — and REACT_APP_FLYWIRE_ENV is not set on
// Vercel, so the default IS the setting. Production ran against the sandbox and nothing
// on screen said so.
//
// This banner is the "you cannot miss it" half of the fix: whenever the app is pointed
// at anything other than the live Flywire environment, the pages that take a payment say
// so. It renders nothing in the normal case, so it costs nothing once the default is
// 'prod'.
//
// Scoped to the pages where a payment is actually taken. It used to render app-wide,
// which put "no money is taken" on /welcome, /login and — worse — on the chromeless
// /link and /offer pages that agents send to their own clients. The warning belongs
// where it changes a decision, not on the public face of the site.
//
// Deliberately inline-styled and dependency-free: it must render even if a stylesheet
// fails to load, and it must never be the thing that breaks a checkout page.
export const PAYMENT_PATHS = ["/reserve", "/reserve-cluster", "/request-to-book-flywire"];

export const isPaymentPath = (pathname) =>
  typeof pathname === "string" && PAYMENT_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

// `env` overrides the frontend constant. The 50/50 scheduled-payment path takes its Flywire
// environment from VT-Backend's response, not from constants.js, so the page passes the
// server's value in — otherwise a non-prod server would route a real deposit to the sandbox
// with no banner, because the frontend constant said 'prod'.
const TestModeBanner = ({ env: envOverride } = {}) => {
  const location = useLocation();
  const env = String(envOverride || constants.FLYWIRE_ENV || "").trim().toLowerCase();
  if (env === "prod") return null;
  if (!isPaymentPath(location.pathname)) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="test-mode-banner"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 2000,
        width: "100%",
        background: "#b02a37",
        color: "#ffffff",
        textAlign: "center",
        padding: "6px 12px",
        fontSize: "13px",
        fontWeight: 600,
        letterSpacing: "0.02em",
        lineHeight: 1.4,
      }}
    >
      TEST MODE - payments go to the Flywire sandbox ({env}) and no money is taken.
    </div>
  );
};

export default TestModeBanner;
